/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { HostBinding, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { isNotNil } from '@gh/core-util';
import { isNil, reverse } from 'lodash';
import { DialogInput } from '../dialog-input';

/**
 * Interface of button provided as input parameter to TextMessageDialog
 */
export interface TextMessageDialogButton<A> {
  /**
   * Value returned to the <code>beforeClose</code> stream when corresponding button has been clicked.
   */
  value: A;
  /**
   * ID used for automation only
   */
  automationId?: string;
  /**
   * Title displayed on the button
   */
  title: string;
  /**
   * Type of this button:
   *   <ul>
   *     <li><code>primary</code> means that this button will be the main raised button
   *       (<code>mat-raised-button</code>)in this dialog</li>
   *     <li><code>icon</code> displays button as <code>mat-icon-button</code></li>
   *     <li>otherwise, button is displayed as flat button (<code>mat-button</code>)</li>
   *   </ul>
   */
  type?: 'primary' | 'icon';
  /**
   * Whether button is disabled or do not
   */
  disabled?: boolean;
}

/**
 * This interface defines input parameters for TextMessageDialog
 */
export interface TextMessageDialogConfig<A> {
  /**
   * Title of the dialog
   */
  title: string;
  /**
   * Optional header of the dialog displayed over the content
   */
  header?: string;
  /**
   * Either text or HTML content should be defined
   */
  text?: string;
  html?: string;
  /**
   * Note displayed in grey in the bottom of the dialog
   */
  note?: string;
  /**
   * Buttons available in this dialog
   */
  buttons: TextMessageDialogButton<A>[];

  width?: number;
  height?: number;
}

export class TextMessageDialogBaseComponent<A> implements OnInit, DialogInput<TextMessageDialogConfig<A>> {
  input: TextMessageDialogConfig<A>;

  private _buttons: TextMessageDialogButton<A>[];

  constructor(private dialogRef: MatDialogRef<any>) {

  }

  @HostBinding('style.width.px')
  get width(): number {
    return this.input.width || 480;
  }

  @HostBinding('style.height')
  get height(): string {
    const { height } = this.input;
    return height ? `${height}px` : 'auto';
  }

  get isTextBased(): boolean {
    return !isNil(this.input.text);
  }

  get isHtmlBased(): boolean {
    return !this.isTextBased;
  }

  get title(): string {
    return this.input.title;
  }

  get header(): Maybe<string> {
    return this.input.header;
  }

  get text(): string {
    return <string>this.input.text;
  }

  get html(): string {
    return <string>this.input.html;
  }

  get buttons(): TextMessageDialogButton<A>[] {
    return this.input.buttons;
  }

  get hasNote(): boolean {
    return isNotNil(this.input.note);
  }

  get note(): Maybe<string> {
    return this.input.note;
  }

  ngOnInit(): void {
    this._buttons = reverse(this.input.buttons);
  }

  onAnswer(button: TextMessageDialogButton<A>): void {
    this.dialogRef.close(button.value);
  }

  getButtonAutomationId(button: TextMessageDialogButton<A>): string {
    const { automationId, value } = button;
    return (automationId || (value || 'gen') as string).toString();
  }
}
